// Onboarding wizard — clone branding, record/upload a voice, choose offered plans,
// toggle content areas. Reads/writes /onboarding; branding → POST /onboarding/branding;
// voice → POST /onboarding/voice; plans → /plans/config; content → /settings.
import { useEffect, useState } from 'react';
import type { AgencySettings, Onboarding as OnboardingData, Plan, SubAccount } from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { Button, Card, ErrorNote, Spinner, Toggle } from './ui';
import { ThemePicker } from '../../components/ThemePicker';
import { applyTheme, cachedThemeId, type ThemeId } from '../../themes';

const STEPS = ['Branding', 'Look', 'Voice', 'Plans', 'Sub-Accounts', 'Content'] as const;

export default function Onboarding({ onDone }: { onDone?: () => void } = {}) {
  const [ob, setOb] = useState<OnboardingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    agencyApi
      .onboarding()
      .then((o) => {
        setOb(o);
        setStep(Math.min(o.step ?? 0, STEPS.length - 1));
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  async function goTo(next: number) {
    const clamped = Math.max(0, Math.min(next, STEPS.length - 1));
    setStep(clamped);
    try {
      const updated = await agencyApi.saveOnboarding({ step: clamped });
      setOb(updated);
    } catch {
      /* non-fatal — step is a local convenience */
    }
  }

  if (error) return <ErrorNote error={error} />;
  if (!ob) return <Spinner />;

  return (
    <div className="max-w-2xl space-y-6">
      {/* Stepper */}
      <ol className="flex items-center gap-2">
        {STEPS.map((label, i) => {
          // Done = you have moved past it, or its own completion flag is set. NOT "the wizard
          // was finished once" — that painted every step green while step 4 still needed input.
          const done =
            i < step ||
            (i === 0 && ob.branding_cloned) ||
            (i === 2 && ob.voice_cloned);
          const active = i === step;
          return (
            <li key={label} className="flex flex-1 items-center gap-2">
              <button
                onClick={() => goTo(i)}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition ${
                  active
                    ? 'bg-brand text-brand-fg'
                    : done
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-500'
                }`}
              >
                {done && !active ? '✓' : i + 1}
              </button>
              <span className={`text-sm ${active ? 'font-medium text-slate-900' : 'text-slate-500'}`}>{label}</span>
              {i < STEPS.length - 1 && <span className="mx-1 hidden h-px flex-1 bg-slate-200 sm:block" />}
            </li>
          );
        })}
      </ol>

      {ob.completed_at && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
          <span>Setup is complete — you are re-visiting it.</span>
          {onDone && (
            <button onClick={onDone} className="font-medium underline underline-offset-2 hover:no-underline">
              Back to the console
            </button>
          )}
        </div>
      )}

      <Card className="p-6">
        {step === 0 && <BrandingStep ob={ob} onDone={(o) => { setOb(o); goTo(1); }} />}
        {step === 1 && <LookStep onNext={() => goTo(2)} onBack={() => goTo(0)} />}
        {step === 2 && <VoiceStep ob={ob} onDone={(o) => { setOb(o); goTo(3); }} onSkip={() => goTo(3)} />}
        {step === 3 && <PlansStep onNext={() => goTo(4)} onBack={() => goTo(2)} />}
        {step === 4 && <WidgetPlacementStep onNext={() => goTo(5)} onBack={() => goTo(3)} />}
        {step === 5 && (
          <ContentStep
            onBack={() => goTo(4)}
            onFinish={async () => {
              const updated = await agencyApi.saveOnboarding({ step: STEPS.length - 1, completed_at: new Date().toISOString() });
              setOb(updated);
              onDone?.();   // a one-time gate hands control back instead of lingering
            }}
          />
        )}
      </Card>
    </div>
  );
}

// ── Step 1: Branding ───────────────────────────────────────────────
// Two names, not one. `appName` is what their white-labeled HighLevel is called (what a sub-account
// user sees in the sidebar); `businessName` is the agency entity. Narration uses both in different
// sentences, so collapsing them into one field put the wrong noun in the wrong line.
function BrandingStep({ ob, onDone }: { ob: OnboardingData; onDone: (o: OnboardingData) => void }) {
  const [whitelabelUrl, setWhitelabelUrl] = useState('');
  const [appName, setAppName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [hlTheme, setHlTheme] = useState<'light' | 'dark'>('light');
  const [accent, setAccent] = useState('#4f46e5');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function clone() {
    setBusy(true);
    setError(null);
    try {
      const updated = await agencyApi.cloneBranding({
        whitelabelUrl: whitelabelUrl.trim() || undefined,
        appName: appName.trim() || undefined,
        businessName: businessName.trim() || undefined,
        hlTheme,
        accent,
      });
      onDone(updated);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Clone your branding</h3>
        <p className="mt-1 text-sm text-slate-500">
          Enter your white-label domain and we'll pull your logo and accent colour automatically.
        </p>
      </div>
      {ob.branding_cloned && (
        <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Branding already cloned ✓</div>
      )}
      <label className="block">
        <span className="text-sm font-medium text-slate-700">White-label URL</span>
        <input
          value={whitelabelUrl}
          onChange={(e) => setWhitelabelUrl(e.target.value)}
          placeholder="https://app.youragency.com"
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">App name</span>
          <input
            value={appName}
            onChange={(e) => setAppName(e.target.value)}
            placeholder="Command Center"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
          <span className="mt-1 block text-xs text-slate-500">
            What your white-labeled HighLevel is called. This is the name your sub-account users see, and
            what narration says in lines like “open <em>Command Center</em>”.
          </span>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Business name</span>
          <input
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="FullStack Coordination"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
          <span className="mt-1 block text-xs text-slate-500">
            Your agency or company. Used where the sentence is about <em>you</em> rather than the software —
            “<em>FullStack Coordination</em> is here to help”.
          </span>
        </label>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-slate-700">Accent colour</span>
        <div className="mt-1 flex items-center gap-2">
          <input
            type="color"
            value={accent}
            onChange={(e) => setAccent(e.target.value)}
            className="h-9 w-12 cursor-pointer rounded border border-slate-200"
          />
          <input
            value={accent}
            onChange={(e) => setAccent(e.target.value)}
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
        </div>
      </label>

      {/* Theme — recordings are made against the real app, so this has to match what users see. */}
      <div>
        <span className="text-sm font-medium text-slate-700">HighLevel theme</span>
        <p className="mt-0.5 text-xs text-slate-500">
          Which theme your account runs (Settings → Company → Theme). We record against the live app, so
          your videos and screenshots come out looking like the platform your users actually log into.
        </p>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {(['light', 'dark'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setHlTheme(t)}
              className={`rounded-xl border-2 p-3 text-left transition ${
                hlTheme === t ? 'border-brand ring-1 ring-brand' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <span
                  className={`inline-block h-3 w-3 rounded-full border ${
                    hlTheme === t ? 'border-brand bg-brand' : 'border-slate-300'
                  }`}
                />
                {t === 'light' ? 'Light theme' : 'Dark theme'}
              </span>
              {/* Miniature of the HighLevel chrome: sidebar + content, in that theme. */}
              <span className={`mt-2 flex h-20 overflow-hidden rounded-md border ${t === 'dark' ? 'border-slate-700' : 'border-slate-200'}`}>
                <span className={`w-1/4 ${t === 'dark' ? 'bg-slate-900' : 'bg-white'}`}>
                  <span className={`mt-2 ml-1.5 block h-1.5 w-8 rounded ${t === 'dark' ? 'bg-slate-700' : 'bg-slate-200'}`} />
                  <span className={`mt-1.5 ml-1.5 block h-1.5 w-6 rounded ${t === 'dark' ? 'bg-slate-700' : 'bg-slate-200'}`} />
                  <span className={`mt-1.5 ml-1.5 block h-1.5 w-7 rounded ${t === 'dark' ? 'bg-slate-700' : 'bg-slate-200'}`} />
                </span>
                <span className={`flex-1 ${t === 'dark' ? 'bg-slate-800' : 'bg-slate-50'}`} />
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Multi-Branding — HighLevel shipped this to beta 2026-07-30 (up to two white-label brands,
          assignable to SaaS plans and sub-accounts). Stubbed deliberately: the button is present so
          the shape of the feature is visible, but disabled until HighLevel ships it broadly. */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-slate-800">Additional brands</div>
            <p className="mt-0.5 text-xs text-slate-500">
              HighLevel now supports up to two white-label brands, assignable to SaaS plans and sub-accounts.
              When it leaves beta you'll be able to add a second brand here — separate logo, colours and
              videos — as a paid add-on.
            </p>
          </div>
          <Button variant="ghost" disabled title="Available when HighLevel Multi-Branding leaves beta">
            + Add brand
          </Button>
        </div>
        <div className="mt-2 inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600">
          Coming soon · HighLevel beta
        </div>
      </div>

      {error && <ErrorNote error={error} />}
      <div className="flex justify-end gap-2">
        <Button onClick={clone} disabled={busy}>
          {busy ? 'Cloning…' : ob.branding_cloned ? 'Re-clone & continue' : 'Clone branding & continue'}
        </Button>
      </div>
    </div>
  );
}

// ── Step 2: Voice ──────────────────────────────────────────────────
// Was an upload-samples-and-clone form. That put us in the middle of the agency's relationship with
// ElevenLabs — their voice, their consent, their bill — and gave them nothing to manage afterwards.
// Instead they bring their own key (optional), and we explain the one decision that actually costs
// money: Starter (Instant Voice Cloning) vs Creator (Professional Voice Cloning).
function VoiceStep({
  ob,
  onDone,
  onSkip,
}: {
  ob: OnboardingData;
  onDone: (o: OnboardingData) => void;
  onSkip: () => void;
}) {
  const [key, setKey] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await agencyApi.saveVoice({
        ...(key.trim() ? { elevenlabs_key: key.trim() } : {}),
        elevenlabs_voice_id: voiceId.trim() || null,
      });
      setSaved(true);
      setKey('');            // never keep the secret in component state after it is stored
      onDone(ob);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Narration voice <span className="font-normal text-slate-400">— optional</span></h3>
        <p className="mt-1 text-sm text-slate-500">
          Your tutorial videos are narrated. By default they use our standard voice, which is included and
          needs no setup. Add your own ElevenLabs key if you'd rather they were narrated in your voice.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <span className="font-medium text-slate-800">You can skip this entirely.</span> Everything works
        without it — you can add a key later from Settings → Voice at any time.
      </div>

      {/* Step-by-step. A video will exist too, but written steps are skimmable and linkable. */}
      <div>
        <h4 className="text-sm font-semibold text-slate-800">Getting your API key</h4>
        <ol className="mt-2 space-y-2 text-sm text-slate-600">
          {[
            <>Go to <a href="https://elevenlabs.io" target="_blank" rel="noreferrer" className="text-brand underline underline-offset-2">elevenlabs.io</a> and create an account (or sign in).</>,
            <>Pick a paid plan — see the comparison below. Voice cloning is not on the free tier.</>,
            <>Click your profile picture, bottom-left, then <span className="font-medium">API Keys</span>.</>,
            <>Choose <span className="font-medium">Create API Key</span>, give it a name like “Support Specialist”, and copy it. ElevenLabs shows the key once — copy it before closing the dialog.</>,
            <>Paste it below and save. We store it encrypted and never show it again.</>,
          ].map((li, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-semibold text-slate-700">
                {i + 1}
              </span>
              <span>{li}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* The plan decision, which is the part people get wrong and pay for. */}
      <div>
        <h4 className="text-sm font-semibold text-slate-800">Which ElevenLabs plan?</h4>
        <p className="mt-0.5 text-xs text-slate-500">
          The difference that matters is how they clone a voice.
        </p>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold text-slate-900">Starter</span>
              <span className="text-xs text-slate-500">Instant Voice Cloning</span>
            </div>
            <p className="mt-1.5 text-xs text-slate-600">
              Clones from a couple of minutes of audio, ready in seconds. Good, clearly recognisable as you,
              and fine for tutorial narration. Occasionally flattens on unusual words.
            </p>
            <p className="mt-1.5 text-xs font-medium text-slate-700">Pick this if you want your voice on your videos without much fuss.</p>
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold text-slate-900">Creator</span>
              <span className="text-xs text-slate-500">Professional Voice Cloning</span>
            </div>
            <p className="mt-1.5 text-xs text-slate-600">
              Trains on around 30 minutes of clean audio and takes a few hours. Noticeably closer to you —
              it holds up across long narration and handles emphasis far better.
            </p>
            <p className="mt-1.5 text-xs font-medium text-slate-700">Pick this if the videos are front-of-house and your voice is part of the brand.</p>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Either works with us. Starter is the sensible starting point — you can upgrade later and swap the
          voice ID without re-recording anything.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">ElevenLabs API key</span>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="sk_…"
            autoComplete="off"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
          <span className="mt-1 block text-xs text-slate-500">Stored encrypted. Never displayed again.</span>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Voice ID <span className="font-normal text-slate-400">— optional</span></span>
          <input
            value={voiceId}
            onChange={(e) => setVoiceId(e.target.value)}
            placeholder="e.g. nPczCjzI2devNBz1zQrb"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
          <span className="mt-1 block text-xs text-slate-500">
            From ElevenLabs → Voices → your voice → ID. Leave blank to use our standard voice.
          </span>
        </label>
      </div>

      {saved && <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Voice settings saved ✓</div>}
      {error && <ErrorNote error={error} />}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onSkip}>Skip for now</Button>
        <Button onClick={save} disabled={busy || (!key.trim() && !voiceId.trim())}>
          {busy ? 'Saving…' : 'Save & continue'}
        </Button>
      </div>
    </div>
  );
}

// ── Step 3: Plans ──────────────────────────────────────────────────
// The toggle looked like a preference; it is actually a content-gating decision. An offered plan is
// one your customers can be nudged toward, and it changes what the help widget shows a user who is
// not on it. Explaining that here beats having someone discover it from a confused client.
function PlansStep({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    agencyApi
      .plansConfig()
      .then((p) => setPlans(p))
      .catch((e: Error) => setError(e.message));
  }, []);

  async function toggle(plan: Plan, offered: boolean) {
    if (!plans) return;
    setPlans(plans.map((p) => (p.id === plan.id ? { ...p, offered } : p)));
    setSavingId(plan.id);
    try {
      const updated = await agencyApi.savePlansConfig([{ id: plan.id, offered }]);
      if (updated.length) setPlans(updated);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Choose the plans you offer</h3>
        <p className="mt-1 text-sm text-slate-500">
          This decides which plans your customers can be nudged toward — and what the help widget shows
          someone who is not on them yet.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
          <div className="text-sm font-semibold text-emerald-900">Leave a plan ON when…</div>
          <ul className="mt-1.5 space-y-1 text-xs text-emerald-900/80">
            <li>• you actively sell it and want customers upgraded into it</li>
            <li>• you're happy for users on lower plans to see what it unlocks</li>
            <li>• its features should appear in help content as “available on …”</li>
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-sm font-semibold text-slate-800">Turn a plan OFF when…</div>
          <ul className="mt-1.5 space-y-1 text-xs text-slate-600">
            <li>• it's retired, private, or a one-off deal for a single client</li>
            <li>• you don't want it advertised to everyone else</li>
            <li>• you're still building it and it isn't ready to be sold</li>
          </ul>
        </div>
      </div>

      {/* What this actually does, shown rather than described. */}
      <div className="rounded-xl border border-slate-200 p-4">
        <div className="text-sm font-semibold text-slate-800">What your users see</div>
        <p className="mt-0.5 text-xs text-slate-500">
          When someone opens help for a feature their plan doesn't include, the widget shows this instead of
          a dead end. Offered plans are what it can point them toward.
        </p>
        <div className="mt-3 flex justify-center">
          {/* Mock of the widget's locked-feature card — deliberately a mock, not a screenshot, so it
              cannot drift out of date as the real widget changes. */}
          <div className="w-full max-w-sm overflow-hidden rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 bg-slate-900 px-3 py-2">
              <span className="h-5 w-5 rounded-full bg-brand" />
              <span className="text-xs font-medium text-white">Help</span>
            </div>
            <div className="space-y-2 bg-white p-3">
              <div className="flex items-center gap-2">
                <span aria-hidden className="text-sm">🔒</span>
                <span className="text-sm font-semibold text-slate-800">Workflows</span>
              </div>
              <p className="text-xs text-slate-500">
                This feature isn't part of your current plan. It's included on <span className="font-medium text-slate-700">Pro</span>.
              </p>
              <div className="flex gap-2 pt-1">
                <span className="rounded-md bg-brand px-2.5 py-1 text-[11px] font-medium text-brand-fg">See Pro</span>
                <span className="rounded-md border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600">Not now</span>
              </div>
            </div>
          </div>
        </div>
        <p className="mt-3 text-center text-xs text-slate-400">
          With every plan off, the widget simply hides the feature instead of offering an upgrade.
        </p>
      </div>

      {error && <ErrorNote error={error} />}
      {!plans && !error && <Spinner />}
      {plans && plans.length === 0 && (
        <p className="text-sm text-slate-400">No plans synced yet — you can sync them later from the Plans tab.</p>
      )}
      {plans && plans.length > 0 && (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {plans.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="font-medium text-slate-800">{p.name}</div>
                <div className="text-xs text-slate-500">{p.price ?? 'Custom'}{p.groupKey ? ` · ${p.groupKey}` : ''}</div>
              </div>
              <Toggle checked={p.offered} onChange={(v) => toggle(p, v)} disabled={savingId === p.id} />
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>← Back</Button>
        <Button onClick={onNext}>Continue</Button>
      </div>
    </div>
  );
}

// ── Step 4: Content ────────────────────────────────────────────────
// "Choose your look" — sits right after Branding so every preview already wears the agency's
// accent. Selection applies instantly (the whole wizard restyles); Continue persists it.
function LookStep({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const [selected, setSelected] = useState<ThemeId>(() => cachedThemeId());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pick(id: ThemeId) {
    setSelected(id);
    applyTheme(id);
  }

  async function next() {
    setBusy(true);
    setError(null);
    try {
      await agencyApi.saveTheme(selected);
      onNext();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-display text-base font-semibold text-ink">Choose your look</h3>
        <p className="mt-1 text-sm text-muted">
          The style of your console — your brand color stays yours in every one. Change it any time
          in Settings.
        </p>
      </div>
      <ThemePicker value={selected} onSelect={pick} />
      {error && <ErrorNote error={error} />}
      <div className="flex items-center justify-between">
        <Button variant="secondary" onClick={onBack}>Back</Button>
        <Button onClick={next} disabled={busy}>{busy ? 'Saving…' : 'Continue'}</Button>
      </div>
    </div>
  );
}

function ContentStep({ onBack, onFinish }: { onBack: () => void; onFinish: () => Promise<void> }) {
  const [s, setS] = useState<AgencySettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    agencyApi
      .settings()
      .then((v) =>
        setS(
          v ?? {
            widget_enabled: true,
            widget_docs_enabled: true,
            widget_videos_enabled: true,
            widget_tours_enabled: true,
            widget_tickets_enabled: true,
            widget_plans_enabled: true,
            widget_ftux_enabled: false,
            widget_position: 'bottom-right',
            widget_theme: 'dark',
            upgrade_url: null,
            default_billable: true,
            subaccount_url_template: null,
            live_support_mode: null,
            live_support_url: null,
            coverage_mode: 'grey_now',
            staleness_mode: 'branded_wins',
            brand_docs: true,
            brand_screenshots: true,
            white_videos: false,
            agency_theme: 'console',
            elevenlabs_voice_id: null,
          },
        ),
      )
      .catch((e: Error) => setError(e.message));
  }, []);

  // Must stay in step with WIDGET_TOGGLES in Settings.tsx — the wizard and Settings are the same
  // switches, and a user who sets six here then finds five there (or vice versa) reasonably assumes
  // one of the screens is lying. Descriptions match too, so the wording is not a second thing to
  // reconcile. The master widget switch is deliberately absent: it is not a content area, and the
  // wizard is not the place to turn the whole widget off.
  const areas: [keyof AgencySettings, string, string][] = [
    ['widget_docs_enabled', 'Help articles', 'Show help articles in the widget'],
    ['widget_videos_enabled', 'Tutorial videos', 'Show tutorial videos'],
    ['widget_tours_enabled', 'Guided tours', 'Show guided product tours'],
    ['widget_tickets_enabled', 'Support tickets', 'Let users submit and track support tickets'],
    ['widget_plans_enabled', 'Plan nudges', 'Show plan upgrade nudges'],
    ['widget_ftux_enabled', 'Welcome tour (FTUX)', "Auto-run a first-time welcome tour on each user's first visit"],
  ];

  async function finish() {
    if (!s) return;
    setBusy(true);
    setError(null);
    try {
      await agencyApi.saveSettings(s);
      await onFinish();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Turn on content areas</h3>
        <p className="mt-1 text-sm text-slate-500">
          Pick what your embedded widget surfaces to end users. These are the same switches as
          Settings → Widget — change your mind there any time.
        </p>
      </div>
      {error && <ErrorNote error={error} />}
      {!s && !error && <Spinner />}
      {s && (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {areas.map(([key, label, hint]) => (
            <div key={key} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <div className="font-medium text-slate-800">{label}</div>
                <div className="text-xs text-slate-500">{hint}</div>
              </div>
              <Toggle
                checked={Boolean(s[key])}
                onChange={(v) => setS({ ...s, [key]: v })}
              />
            </div>
          ))}
        </div>
      )}
      {s && (
        <p className="text-xs text-slate-400">
          Some areas also depend on your Support Specialist plan — anything not included on your tier stays
          off and is marked in Settings.
        </p>
      )}
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>← Back</Button>
        <Button onClick={finish} disabled={busy || !s}>
          {busy ? 'Saving…' : 'Finish onboarding'}
        </Button>
      </div>
    </div>
  );
}


// ── Step 4: Sub-Accounts — where should the widget appear? ─────────
// This is asked at setup because the widget's delivery model surprises people: it loads from
// agency-wide Custom JS, so it is present in EVERY sub-account by default. Deciding here beats
// discovering it when a client asks why a support bubble appeared in their account.
function WidgetPlacementStep({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const [rows, setRows] = useState<SubAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    try { setRows(await agencyApi.accounts()); setError(null); }
    catch (e) { setError((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);

  const sync = async () => {
    setBusy('sync');
    try { await agencyApi.syncAccounts(); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  const toggle = async (row: SubAccount, next: boolean) => {
    setRows((cur) => (cur ?? []).map((r) => (r.location_id === row.location_id ? { ...r, widget_enabled: next } : r)));
    try { await agencyApi.setAccountWidget(row.location_id, next); }
    catch (e) {
      setRows((cur) => (cur ?? []).map((r) => (r.location_id === row.location_id ? { ...r, widget_enabled: !next } : r)));
      setError((e as Error).message);
    }
  };

  const bulk = async (enabled: boolean) => {
    setBusy('bulk');
    try { await agencyApi.setWidgetBulk(enabled); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  const on = (rows ?? []).filter((r) => r.widget_enabled).length;

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900">Where should the support widget appear?</h3>
      <p className="mt-1 text-sm text-slate-500">
        The widget loads from your agency-wide Custom JS, so it reaches every sub-account by
        default. Switch off any account that should not see it.
      </p>

      {error && <div className="mt-4"><ErrorNote error={error} /></div>}

      <div className="mt-4 flex items-center gap-2">
        <Button variant="secondary" onClick={sync} disabled={busy === 'sync'}>
          {busy === 'sync' ? 'Syncing…' : 'Sync from HighLevel'}
        </Button>
        <Button variant="ghost" onClick={() => bulk(true)} disabled={busy === 'bulk' || !rows?.length}>All on</Button>
        <Button variant="ghost" onClick={() => bulk(false)} disabled={busy === 'bulk' || !rows?.length}>All off</Button>
        {!!rows?.length && (
          <span className="ml-auto text-xs text-slate-500">{on} of {rows.length} on</span>
        )}
      </div>

      {!rows ? (
        <div className="mt-4"><Spinner label="Loading sub-accounts…" /></div>
      ) : !rows.length ? (
        <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          No sub-accounts yet. <strong>Sync from HighLevel</strong> pulls the roster — you can also
          do this later from the Sub-Accounts section.
        </p>
      ) : (
        <div className="mt-4 max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200">
          {rows.map((r) => (
            <label key={r.location_id} className="flex items-center gap-3 px-4 py-2.5">
              <Toggle checked={r.widget_enabled} onChange={(v) => toggle(r, v)} />
              <span className="flex-1 text-sm text-slate-800">{r.client_name ?? r.location_id}</span>
              <span className="font-mono text-[11px] text-slate-400">{r.location_id}</span>
            </label>
          ))}
        </div>
      )}

      <div className="mt-6 flex gap-2">
        <Button variant="secondary" onClick={onBack}>Back</Button>
        <Button onClick={onNext}>Continue</Button>
      </div>
    </div>
  );
}
